import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import logo from "@/assets/logo-horizontal.png";
import LanguageSelector from "@/components/LanguageSelector";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/hooks/useTranslation";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { getLocalizedPath } = useLanguage();
  const { t } = useTranslation();
  const nav = t("nav");

  // Reset scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { to: "/", label: nav.home },
    { to: "/chi-siamo", label: nav.about },
    { to: "/servizi", label: nav.services },
    { to: "/portfolio", label: nav.portfolio },
    { to: "/contatti", label: nav.contact },
  ];

  const isConfiguratorPage = location.pathname === "/preventivo" || location.pathname === getLocalizedPath("/preventivo");

  const isActive = (path: string) => {
    const localizedPath = getLocalizedPath(path);
    return location.pathname === localizedPath || location.pathname === path;
  };

  return (
    <header
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled 
          ? "bg-background/98 backdrop-blur-md shadow-sm border-b border-border/50" 
          : "bg-background/95 backdrop-blur-sm"
      }`}
    >
      <div className="container mx-auto px-3 sm:px-4 lg:px-8">
        <div className="flex items-center justify-between py-0">
          <Link to={getLocalizedPath("/")} className="flex items-center group">
            <img 
              src={logo} 
              alt="pannelliespositori.it" 
              width={360}
              height={90}
              className="h-[72px] sm:h-[84px] md:h-[100px] w-auto transition-transform duration-300 group-hover:scale-[1.02]" 
            />
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <nav className="flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={getLocalizedPath(link.to)}
                  className={`relative px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive(link.to)
                      ? "text-primary bg-primary/5"
                      : "text-foreground/80 hover:text-foreground hover:bg-muted/50"
                  }`}
                >
                  {link.label}
                  {isActive(link.to) && (
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary" />
                  )}
                </Link>
              ))}
            </nav>
             {/* Configura Online button - styled like other header links */}
             <Link
               to={getLocalizedPath("/preventivo")}
               className={`relative ml-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                 isConfiguratorPage
                   ? "text-primary bg-primary/5"
                   : "text-foreground/80 hover:text-foreground hover:bg-muted/50"
               }`}
             >
               Configura Online
               {isConfiguratorPage && (
                 <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary" />
               )}
             </Link>
            <div className="ml-2 pl-2 border-l border-border/50">
              <LanguageSelector />
            </div>
          </div>

          <div className="md:hidden flex items-center gap-2">
            <LanguageSelector />
            <button
              className="text-foreground p-2 rounded-lg hover:bg-muted/50 transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? nav.closeMenu : nav.openMenu}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-300 ease-out ${
            isMobileMenuOpen ? "max-h-[400px] opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <nav className="py-3 border-t border-border/50 bg-background">
            <div className="flex flex-col gap-0.5">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={getLocalizedPath(link.to)}
                  className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 ${
                    isActive(link.to)
                      ? "text-primary bg-primary/5"
                      : "text-foreground/80 hover:text-foreground hover:bg-muted/50"
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
               {/* Configura Online link for mobile - styled like other items */}
               <Link
                 to={getLocalizedPath("/preventivo")}
                 className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 ${
                   isConfiguratorPage
                     ? "text-primary bg-primary/5"
                     : "text-foreground/80 hover:text-foreground hover:bg-muted/50"
                 }`}
                 onClick={() => setIsMobileMenuOpen(false)}
               >
                 Configura Online
               </Link>
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
